import { useMutation } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Check, Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { type FormEvent, useEffect, useRef, useState } from "react";

import {
  ApiError,
  isMfaChallenge,
  login,
  loginMfa,
  type LoginResult,
  type MfaEnrolment,
} from "../api/client";
import { oidcClient } from "../auth/oidc-config";
import { useCurrentUser } from "../auth/useCurrentUser";
import { AuthShell } from "../components/AuthShell";
import { PageLoader } from "../components/BrandedSpinner";
import { LoginMarketingPanel } from "../components/auth/LoginMarketingPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FormError } from "@/components/ui/form-error";

/**
 * PRM-rendered sign-in card (ADR-0010).
 *
 * The form posts {email, password, remember_me, oidc_authorize_url} to
 * `POST /auth/login`. Backend drives Authentik's flow executor API
 * server-to-server and returns a redirect URL on the PRM origin
 * (`/auth/callback?code=...`). The browser navigates there and the
 * existing AuthCallback finishes the OIDC code exchange.
 *
 * Layout matches `docs/Epic 1/RAC-User Flow - Channel Partner Access
 * Request-040526-024717.pdf`: PARTNER SIGN-IN overline → Welcome back
 * heading → Work email + Password fields → Keep me signed in checkbox
 * → Sign in button → footer.
 */

const SITE_LOCATION = "AU-SYD";

export function LoginRoute() {
  const { isAuthenticated, data: me, error: meError } = useCurrentUser();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // CHP-562: default unchecked — persistent sessions are opt-in on a B2B
  // portal where shared/kiosk workstations are plausible (product decision
  // 2026-07-12).
  const [rememberMe, setRememberMe] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  // CHP-508: when staff login pauses at the TOTP gate, the card switches to
  // the MFA step. `mfaKind` is "validate" (enrolled device) or "enrol"
  // (first-login setup, with `enrolment` carrying the otpauth key).
  const [step, setStep] = useState<"credentials" | "mfa">("credentials");
  const [mfaToken, setMfaToken] = useState("");
  const [mfaKind, setMfaKind] = useState<"validate" | "enrol">("validate");
  const [enrolment, setEnrolment] = useState<MfaEnrolment | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  // CHP-809: transient confirmation after copying the manual-entry TOTP secret.
  const [secretCopied, setSecretCopied] = useState(false);

  // CHP-419: a session that ends lands the user here as /login?expired=1
  // (see auth/session-expiry.ts endSession). Capture the flag once for the
  // banner, then strip it from the URL so a refresh — or a failed sign-in
  // attempt that keeps us on this page — doesn't keep re-announcing it.
  const { searchStr } = useLocation();
  const navigate = useNavigate();
  const [sessionExpired] = useState(
    () => new URLSearchParams((searchStr || "").replace(/^\?/, "")).get("expired") === "1",
  );
  useEffect(() => {
    if (sessionExpired) {
      void navigate({ to: "/login", replace: true });
    }
  }, [sessionExpired, navigate]);

  // CHP-1203: ONE redirect destination for an already-authenticated visitor,
  // mirroring ProtectedRoute's decision order — alert-ish states (me-fetch
  // error / deactivated / pending) go to /dashboard where ProtectedRoute
  // renders the explanatory state; only the onboarding cases route directly.
  // Null = not decidable yet (me still loading).
  const authedTarget = !isAuthenticated
    ? null
    : !me && !meError
      ? null
      : me && me.is_active && me.status !== "pending"
        ? me.tc_accepted_at == null
          ? "/onboarding/identity"
          : me.tc_reaccept_required
            ? "/onboarding/terms"
            : "/dashboard"
        : "/dashboard";

  // Fire the redirect imperatively, exactly once (ref-guarded), instead of
  // rendering a <Navigate>. A render-time <Navigate> re-navigates on every
  // re-render the transition itself causes — the "Maximum update depth
  // exceeded" loop this ticket fixes (~50 console errors per visit). An
  // effect keyed on the resolved target navigates once and the loop
  // mechanism is gone entirely.
  const redirected = useRef(false);
  useEffect(() => {
    if (authedTarget === null || redirected.current) return;
    redirected.current = true;
    void navigate({ to: authedTarget, replace: true });
  }, [authedTarget, navigate]);

  function handleLoginResult(result: LoginResult) {
    if (isMfaChallenge(result)) {
      setMfaToken(result.mfa_token);
      setMfaKind(result.kind);
      setEnrolment(result.enrolment ?? null);
      setMfaCode("");
      setTopError(null);
      setStep("mfa");
      return;
    }
    // Plain redirect — synchronous navigate so AuthProvider picks up the
    // code on the new page-load.
    window.location.assign(result.redirect_url);
  }

  const submit = useMutation({
    mutationFn: async () => {
      // Build the OIDC authorize URL without navigating. This stashes
      // PKCE state + nonce in sessionStorage so a later
      // userManager.signinCallback() can complete the code exchange.
      // request_type "si:r" must be set explicitly — UserManager's
      // signinCallback() switches on this field to dispatch to
      // signinRedirectCallback (vs popup/silent), and a missing
      // value throws "invalid request_type in state".
      const req = await oidcClient.createSigninRequest({ request_type: "si:r" });
      return login({
        email: email.trim().toLowerCase(),
        password,
        remember_me: rememberMe,
        oidc_authorize_url: req.url,
      });
    },
    onSuccess: handleLoginResult,
    onError: (err) => {
      // Always log the raw error so DevTools console captures the
      // root cause — fetch network errors, OIDC discovery failures,
      // and unexpected statuses are otherwise indistinguishable from
      // wrong-password to a user.
      console.error("login failed:", err);
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setTopError("Email or password is incorrect.");
          return;
        }
        if (err.status === 429) {
          setTopError("Too many sign-in attempts. Wait a minute and try again.");
          return;
        }
        if (err.status === 502) {
          setTopError(
            "We can't reach the authentication service right now. Please try again shortly.",
          );
          return;
        }
        // Surface unexpected API status codes so we can diagnose
        // (rather than burying them under a generic message).
        setTopError(`Sign-in failed (${err.status}). Check the browser console for details.`);
        return;
      }
      // Non-ApiError: fetch network failure, OIDC discovery failure,
      // or createSigninRequest throw. Surface the error type so the
      // user can report it usefully.
      const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      setTopError(`Sign-in failed: ${msg}`);
    },
  });

  const mfaSubmit = useMutation({
    mutationFn: async () => loginMfa({ mfa_token: mfaToken, code: mfaCode.trim() }),
    onSuccess: handleLoginResult,
    onError: (err) => {
      console.error("mfa verification failed:", err);
      if (err instanceof ApiError) {
        if (err.status === 401) {
          const detail =
            err.body && typeof err.body === "object" && "detail" in err.body
              ? String((err.body as { detail?: unknown }).detail ?? "")
              : "";
          // A dead token (expired / attempt-cap burned) can't be retried —
          // bounce back to credentials. A bad code stays on the MFA step.
          if (detail.includes("expired") || detail.includes("too many")) {
            setStep("credentials");
            setMfaToken("");
            setMfaCode("");
            setTopError("Your sign-in timed out. Please sign in again.");
            return;
          }
          setTopError("That code didn't match. Try again.");
          return;
        }
        if (err.status === 429) {
          setTopError("Too many attempts. Wait a minute and try again.");
          return;
        }
        if (err.status === 502) {
          setTopError(
            "We can't reach the authentication service right now. Please try again shortly.",
          );
          return;
        }
        setTopError(`Verification failed (${err.status}). Check the browser console for details.`);
        return;
      }
      const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      setTopError(`Verification failed: ${msg}`);
    },
  });

  if (isAuthenticated) {
    // Loader both while /auth/me resolves (target not decidable) and while
    // the one-shot redirect effect above carries the transition out.
    return <PageLoader />;
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTopError(null);
    if (!email.trim() || !password) {
      setTopError("Enter your work email and password to sign in.");
      return;
    }
    submit.mutate();
  }

  function onMfaSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTopError(null);
    if (!mfaCode.trim()) {
      setTopError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    mfaSubmit.mutate();
  }

  // Extract the manual-entry key from the otpauth:// config URL as the
  // "can't scan?" fallback under the QR (desktop authenticators, accessibility).
  function otpSecret(configUrl: string): string | null {
    try {
      return new URL(configUrl).searchParams.get("secret");
    } catch {
      return null;
    }
  }

  // CHP-809: copy the manual-entry TOTP secret to the clipboard. Best-effort —
  // navigator.clipboard can reject (insecure context / permissions); on failure
  // the user can still select the text manually. Mirrors the InviteUserDialog
  // copy pattern.
  async function copySecret(secret: string) {
    try {
      await navigator.clipboard.writeText(secret);
      setSecretCopied(true);
      window.setTimeout(() => setSecretCopied(false), 2000);
    } catch {
      setSecretCopied(false);
    }
  }

  // Split-screen renders the marketing panel on the left and the existing
  // card on the right. Card markup is unchanged so all unit + e2e
  // selectors (role/label/text) still resolve identically. The wrappers
  // are inlined (not extracted to a function) because a function defined
  // inside the component body gets a new identity on each render, which
  // would remount the form subtree on every state update and drop
  // pending mutation state.
  // shadow-lifted (in AuthShell's card) gives the auth panel slightly stronger
  // elevation than dashboard cards — soft black + a faint coral glow.
  const card = (
    <AuthShell
      bare
      headingId="login-heading"
      showLogo
      overline="Partner sign-in"
      heading="Welcome back."
      headingClassName="text-3xl font-semibold tracking-tight text-foreground"
      subheading="Sign in to the Partner Portal."
    >
      {sessionExpired && (
        <p
          role="status"
          className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
        >
          Your session has ended — please sign in again.
        </p>
      )}

      <form onSubmit={onSubmit} aria-busy={submit.isPending} noValidate className="space-y-4">
        <div className="space-y-2">
          <Label
            htmlFor="login-email"
            className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
          >
            Work email
          </Label>
          <Input
            id="login-email"
            type="email"
            autoComplete="email"
            autoFocus
            aria-required="true"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="login-password"
            className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
          >
            Password
          </Label>
          <Input
            id="login-password"
            type="password"
            autoComplete="current-password"
            aria-required="true"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            <span>Keep me signed in</span>
          </label>
          <Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline">
            Forgot password?
          </Link>
        </div>

        {topError && <FormError>{topError}</FormError>}

        <Button type="submit" disabled={submit.isPending} size="lg" className="w-full">
          {submit.isPending ? (
            "Signing in…"
          ) : (
            <>
              Sign in <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </form>

      <div className="border-t pt-4 flex justify-between text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
        <span>© 2026 Reset Data Pty Ltd</span>
        <span>{SITE_LOCATION}</span>
      </div>
    </AuthShell>
  );

  // CHP-508 MFA step. Same card shell as the credentials form so selectors and
  // layout stay consistent; shown when staff login pauses at the TOTP gate.
  const mfaCard = (
    <AuthShell
      bare
      headingId="mfa-heading"
      showLogo
      overline="Two-factor authentication"
      heading={mfaKind === "enrol" ? "Set up your authenticator." : "Enter your code."}
      headingClassName="text-3xl font-semibold tracking-tight text-foreground"
      subheading={
        mfaKind === "enrol"
          ? "Add the key below to an authenticator app (Google Authenticator, 1Password, …), then enter the 6-digit code it shows."
          : "Open your authenticator app and enter the current 6-digit code."
      }
    >
      {mfaKind === "enrol" && enrolment && (
        <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
          {/* Intentional literal white, not a token: a QR code needs a light
              quiet-zone to stay scannable in EVERY theme. The QRCodeSVG renders
              dark modules on a transparent ground, so this patch must stay light
              even under the .dark theme — it must NOT follow the card's
              card/background token, which inverts to dark. */}
          <div className="flex justify-center rounded-md bg-white p-3">
            <QRCodeSVG
              value={enrolment.config_url}
              size={160}
              aria-label="Authenticator setup QR code"
            />
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Scan with your authenticator app. Can't scan? Enter this key manually:
          </p>
          {(() => {
            const secret = otpSecret(enrolment.config_url);
            return (
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 break-all font-mono text-xs text-center">
                  {secret ?? enrolment.config_url}
                </code>
                {secret ? (
                  <button
                    type="button"
                    onClick={() => void copySecret(secret)}
                    aria-label={secretCopied ? "Secret copied" : "Copy setup key"}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
                  >
                    {secretCopied ? (
                      <>
                        <Check className="h-3.5 w-3.5 text-success" aria-hidden />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" aria-hidden />
                        Copy
                      </>
                    )}
                  </button>
                ) : null}
              </div>
            );
          })()}
        </div>
      )}

      <form onSubmit={onMfaSubmit} aria-busy={mfaSubmit.isPending} noValidate className="space-y-4">
        <div className="space-y-2">
          <Label
            htmlFor="mfa-code"
            className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
          >
            Authentication code
          </Label>
          <Input
            id="mfa-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            aria-required="true"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
          />
        </div>

        {topError && <FormError>{topError}</FormError>}

        <Button type="submit" disabled={mfaSubmit.isPending} size="lg" className="w-full">
          {mfaSubmit.isPending ? (
            "Verifying…"
          ) : (
            <>
              Verify <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>

        <button
          type="button"
          onClick={() => {
            setStep("credentials");
            setMfaCode("");
            setTopError(null);
          }}
          className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline"
        >
          Back to sign in
        </button>
      </form>

      <div className="border-t pt-4 flex justify-between text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
        <span>© 2026 Reset Data Pty Ltd</span>
        <span>{SITE_LOCATION}</span>
      </div>
    </AuthShell>
  );

  return (
    // Split-screen on lg+: marketing panel left, sign-in card right.
    // On narrow viewports the aside hides and the card centers — same
    // single-column experience as before. The aside carries the
    // .demo-theme scope class so LoginMarketingPanel's internal demo
    // utility classes (.wordmark, .chip, .display, .mono-xs, .pill,
    // .dot) resolve via apps/web/src/demo/theme.css without affecting
    // the rest of the app.
    <div className="grid min-h-screen lg:grid-cols-2">
      <aside className="demo-theme relative hidden flex-col justify-between border-r border-border p-10 lg:flex">
        <LoginMarketingPanel />
      </aside>
      <div className="grid place-items-center p-4 sm:p-10">{step === "mfa" ? mfaCard : card}</div>
    </div>
  );
}
